// Copyright 2018-2023 Observable, Inc.
// Released under the ISC license.
// https://observablehq.com/@d3/pie-chart
import * as d3 from 'd3'

interface PieChartOptions<T> {
  name: (d: T) => string
  value: (d: T) => number
  title?: (d: T, i: number, data: T[]) => string
  width?: number
  height?: number
  innerRadius?: number
  outerRadius?: number
  labelRadius?: number
  format?: string
  names?: string[]
  colors?: string[]
  stroke?: string
  strokeWidth?: number
  strokeLinejoin?: string
  padAngle?: number
  fontSize?: number | string
}

function getColors(colors: string[] | undefined, namesLength: number) {
  if (colors === undefined) {
    // d3.schemeSpectral only supports 3-11 colors, fallback for smaller datasets
    if (namesLength <= 3) {
      return d3.schemeSpectral[namesLength + 3]
    }

    return d3.schemeSpectral[namesLength]
  }

  return colors
}

export default function PieChart<T>(
  data: T[],
  {
    name,
    value,
    title,
    width = 640,
    height = 400,
    innerRadius = 0,
    outerRadius = Math.min(width, height) / 2,
    labelRadius = innerRadius * 0.2 + outerRadius * 0.8,
    format = ',',
    names,
    colors,
    stroke = innerRadius > 0 ? 'none' : 'white',
    strokeWidth = 1,
    strokeLinejoin = 'round',
    padAngle = stroke === 'none' ? 1 / outerRadius : 0,
    fontSize = 14,
  }: PieChartOptions<T>,
) {
  // Compute values.
  const N = d3.map(data, name)
  const V = d3.map(data, value)
  const I = d3.range(N.length).filter((i) => !isNaN(V[i]))

  // Unique the names.
  if (names === undefined) names = Array.from(new Set(N))
  names = Array.from(new Set(names))

  // Choose a default color scheme based on cardinality.
  const colorsFinal = getColors(colors, names.length)

  // Construct scales.
  const color = d3.scaleOrdinal<string, string>().domain(names).range(colorsFinal)

  // Compute titles.
  let computedTitle: (i: number) => string
  if (title === undefined) {
    const formatValue = d3.format(format)
    computedTitle = (i) => `${N[i]}\n${formatValue(V[i])}`
  } else {
    const O = data
    computedTitle = (i) => title!(O[i], i, data)
  }

  // Construct arcs.
  const arcs = d3
    .pie<number>()
    .padAngle(padAngle)
    .sort(null)
    .value((i) => V[i])(I)

  const arc = d3.arc<d3.PieArcDatum<number>>().innerRadius(innerRadius).outerRadius(outerRadius)
  const arcLabel = d3
    .arc<d3.PieArcDatum<number>>()
    .innerRadius(labelRadius)
    .outerRadius(labelRadius)

  const svg = d3
    .create('svg')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', [-width / 2, -height / 2, width, height].toString())
    .attr('style', 'max-width: 100%; height: auto; height: intrinsic;')

  svg
    .append('g')
    .attr('stroke', stroke)
    .attr('stroke-width', strokeWidth)
    .attr('stroke-linejoin', strokeLinejoin)
    .selectAll('path')
    .data(arcs)
    .join('path')
    .attr('fill', (d) => color(N[d.data]))
    .attr('d', arc)
    .append('title')
    .text((d) => computedTitle(d.data))

  svg
    .append('g')
    .attr('font-size', fontSize)
    .attr('text-anchor', 'middle')
    .selectAll('text')
    .data(arcs)
    .join('text')
    .attr('transform', (d) => `translate(${arcLabel.centroid(d)})`)
    .selectAll('tspan')
    .data((d) => {
      const lines = `${computedTitle(d.data)}`.split(/\n/)
      return d.endAngle - d.startAngle > 0.25 ? lines : lines.slice(0, 1)
    })
    .join('tspan')
    .attr('x', 0)
    .attr('y', (_, i) => `${(i + 1) * 1.1}em`)
    .attr('font-weight', (_, i) => (i ? null : 'bold'))
    .text((d) => d)

  return Object.assign(svg.node() as object, { scales: { color } }) as unknown as SVGElement
}
